import { WriteContractParameters } from '@wagmi/core';
import { useMemo } from 'react';
import { useConnection, useReadContract } from 'wagmi';

import { TRANCHE_SHARE_DECIMALS } from '@/app/shared/constants/common';
import { VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import { useSendBatchTransactions } from '@/app/shared/hooks/blockchain/useSendBatchTransactions/useSendBatchTransactions';
import { useDebounce } from '@/app/shared/hooks/useDebounce';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';

interface UseVaultWithdrawArgs {
  amount: string;
  trancheId: VaultTrancheId;
  vaultId?: string;
  enabled?: boolean;
}

export const useVaultWithdraw = ({ amount, trancheId, vaultId, enabled = true }: UseVaultWithdrawArgs) => {
  const { address, isConnected } = useConnection();
  const contracts = useChainContracts(vaultId);
  const debouncedAmount = useDebounce(amount, 500);

  const sharesAmount = safeParseUnits(debouncedAmount, TRANCHE_SHARE_DECIMALS);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;

  const { data: directWithdrawEnabled } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'directWithdrawEnabled',
    query: {
      enabled: enabled && !!trancheAddress,
    },
  });

  const isInstantWithdraw = directWithdrawEnabled === true;

  const { data: previewAssets } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'previewRedeem',
    args: sharesAmount && sharesAmount > 0n ? [sharesAmount] : [0n],
    query: {
      enabled: enabled && !!trancheAddress && !!sharesAmount && sharesAmount > 0n && isInstantWithdraw,
    },
  });

  const transactions = useMemo((): WriteContractParameters[] => {
    if (!enabled || !contracts || !sharesAmount || !trancheAddress || directWithdrawEnabled === undefined) {
      return [];
    }

    const trancheWithdraw: WriteContractParameters = {
      address: trancheAddress,
      abi: ABI.TRANCHE,
      functionName: 'requestWithdrawal',
      args: [sharesAmount],
    };

    if (!isInstantWithdraw) return [trancheWithdraw];
    if (!previewAssets || previewAssets === 0n) return [];

    return [
      trancheWithdraw,
      {
        address: contracts.MZUSD,
        abi: ABI.MZUSD,
        functionName: 'requestWithdrawal',
        args: [previewAssets],
      },
    ];
  }, [
    contracts,
    directWithdrawEnabled,
    enabled,
    isInstantWithdraw,
    previewAssets,
    sharesAmount,
    trancheAddress,
  ]);

  const expectedTxCount = isInstantWithdraw ? 2 : 1;
  const isReady =
    enabled &&
    isConnected &&
    !!address &&
    directWithdrawEnabled !== undefined &&
    transactions.length === expectedTxCount &&
    !!sharesAmount;

  const batch = useSendBatchTransactions({
    transactions,
    enabled: isReady,
  });

  return {
    ...batch,
    sharesAmount,
    previewAssets,
    trancheAddress,
    contracts,
    debouncedAmount,
    isReady,
    isInstantWithdraw,
    directWithdrawEnabled,
  };
};
